import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import { exchangeGoogleCode, getGoogleAuthUrl } from '../config';
import { useAuth } from '../context/AuthContext';

const GOOGLE_CALLBACK_PATH = '/my';
const GOOGLE_NEXT_STORAGE_KEY = 'meteris_google_login_next';

function buildLoginPath(intent, fallbackPath = '/compte/connexion') {
  return intent === 'admin' ? '/my' : fallbackPath;
}

function buildGoogleCallbackPath() {
  return GOOGLE_CALLBACK_PATH;
}

function resolveIntent(stateValue, fallbackIntent) {
  return stateValue === 'admin' || stateValue === 'client' ? stateValue : fallbackIntent;
}

function storeLoginNextPath(intent, nextPath) {
  if (typeof window === 'undefined') return;
  const entryPath = window.location.pathname;
  if (!nextPath && !entryPath) {
    window.sessionStorage.removeItem(GOOGLE_NEXT_STORAGE_KEY);
    return;
  }
  window.sessionStorage.setItem(GOOGLE_NEXT_STORAGE_KEY, JSON.stringify({ intent, nextPath, entryPath }));
}

function readStoredLoginContext(intent) {
  if (typeof window === 'undefined') return null;
  try {
    const rawValue = window.sessionStorage.getItem(GOOGLE_NEXT_STORAGE_KEY);
    if (!rawValue) return null;
    const parsed = JSON.parse(rawValue);
    if (parsed?.intent !== intent || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearStoredNextPath() {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(GOOGLE_NEXT_STORAGE_KEY);
}

export default function GoogleLoginPage({
  intent,
  title,
  subtitle,
  description,
  helperText,
  ctaLabel = 'Continuer avec Google',
  alternateText = '',
  alternateLinkLabel = '',
  alternateLinkTo = '',
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, loginWithSession } = useAuth();
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(location.state?.error || '');

  const params = new URLSearchParams(location.search);
  const code = params.get('code');
  const effectiveIntent = resolveIntent(params.get('state'), intent);
  const storedContext = readStoredLoginContext(effectiveIntent);
  const nextPath = params.get('next') || storedContext?.nextPath || '';
  const loginPath = buildLoginPath(effectiveIntent, storedContext?.entryPath || '/compte/connexion');

  useEffect(() => {
    if (code || authLoading || !user) return;
    if (effectiveIntent === 'admin') {
      if (user.can_access_backoffice) {
        navigate('/my/dashboard', { replace: true });
      } else {
        navigate('/compte', { replace: true });
      }
      return;
    }
    navigate(nextPath || '/compte', { replace: true });
  }, [authLoading, code, effectiveIntent, navigate, nextPath, user]);

  useEffect(() => {
    if (!code) return;
    const redirectUri = `${window.location.origin}${buildGoogleCallbackPath()}`;
    let active = true;

    const completeLogin = async () => {
      setProcessing(true);
      setError('');
      try {
        const data = await exchangeGoogleCode({ code, redirectUri, intent: effectiveIntent });
        if (!active) return;
        await loginWithSession(data.session_token, data);
        clearStoredNextPath();
        window.history.replaceState(null, '', loginPath);
        if (effectiveIntent === 'admin') {
          if (data.can_access_backoffice) {
            navigate('/my/dashboard', { replace: true });
          } else {
            navigate('/compte', { replace: true });
          }
        } else {
          navigate(nextPath || '/compte', { replace: true });
        }
      } catch (err) {
        if (!active) return;
        clearStoredNextPath();
        const message = err.message || 'Erreur de connexion';
        if (effectiveIntent === 'client' && location.pathname === GOOGLE_CALLBACK_PATH) {
          navigate(loginPath, { replace: true, state: { error: message } });
          return;
        }
        setError(message);
        window.history.replaceState(null, '', loginPath);
      } finally {
        if (active) setProcessing(false);
      }
    };

    completeLogin();
    return () => { active = false; };
  }, [code, effectiveIntent, location.pathname, loginPath, loginWithSession, navigate, nextPath]);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      storeLoginNextPath(intent, nextPath);
      const redirectUri = `${window.location.origin}${buildGoogleCallbackPath()}`;
      const data = await getGoogleAuthUrl(redirectUri, intent);
      window.location.href = data.url;
    } catch (err) {
      clearStoredNextPath();
      setError(err.message || 'Erreur de connexion');
      setLoading(false);
    }
  };

  if (processing) {
    return (
      <div className="min-h-screen bg-primary flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-secondary animate-spin mx-auto mb-4" />
          <p className="font-accent uppercase tracking-widest text-xs text-slate-400">Connexion en cours…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo Météris + retour */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center mb-6">
            <img
              src="/logo-meteris.png"
              alt="Météris"
              className="h-16 w-16 rounded-full object-cover ring-2 ring-secondary/30"
            />
          </Link>
          <h1 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight">{title}</h1>
        </div>

        {/* Card form — fond slate-50 (blanc légèrement bleuté pour cohérence ambiance) */}
        <div className="bg-slate-50 text-slate-900 rounded-sm border border-slate-200 shadow-2xl p-6 md:p-8 space-y-5">
          <p className="text-sm text-slate-600 text-center leading-relaxed">{description}</p>

          {error && (
            <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-sm p-3 text-sm text-rose-700">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-white border-2 border-primary text-primary hover:bg-primary hover:text-white font-bold uppercase tracking-wider rounded-sm py-3 text-sm transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
            )}
            <span>{ctaLabel}</span>
          </button>

          {alternateLinkTo && alternateLinkLabel && (
            <p className="text-xs text-slate-500 text-center">
              {alternateText ? `${alternateText} ` : ''}
              <Link to={alternateLinkTo} className="font-accent uppercase tracking-wider text-secondary hover:text-primary transition-colors">
                {alternateLinkLabel}
              </Link>
            </p>
          )}

          {helperText && (
            <p className="text-[10px] text-slate-400 text-center leading-relaxed">{helperText}</p>
          )}
        </div>

        <Link
          to="/"
          className="inline-flex items-center gap-2 mt-6 font-accent text-xs uppercase tracking-widest text-slate-400 hover:text-secondary transition-colors w-full justify-center"
        >
          <ArrowLeft className="w-3 h-3" />
          Retour au site
        </Link>
      </div>
    </div>
  );
}
